import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  doReportingGetReportCsv,
  doReportingMergeDraftFilterValues,
  doReportingShowReportFilterModal,
  doReportingSetReportVisualsSize,
  doReportingGetReportListData,
} from 'store/actions';
import {
  selectRptReportFilterOptions,
  selectRptReportFilterValues,
  selectRptReportHasVisuals,
  selectRptReportName,
  selectRptReportTotalCount,
  selectRptReportVisualsSize,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';
import { IconButton } from 'components/IconButton';
import { smartText } from 'lib/str';
import { ReportingVisualsSizeEnum } from 'types/types';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';
import { ButtonGroup } from 'components/ButtonGroup';

type Report = ReportTypes[ReportEnum.assets];

export const Header: React.FC<{ reportKey: string }> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  // const [widthSize, setWidthSize] = React.useState<
  //   'small' | 'medium' | 'large'
  // >('small');
  const visualsSize = useSelector(selectRptReportVisualsSize);
  const hasVisuals = useSelector((state) =>
    selectRptReportHasVisuals(state, reportKey),
  );
  const reportName = useSelector((state) =>
    selectRptReportName(state, reportKey),
  );
  const reportTotalCount = useSelector((state) =>
    selectRptReportTotalCount(state, reportKey),
  );

  const filterValues = useSelector((state) =>
    selectRptReportFilterValues(state, reportKey),
  )! as Report['filterValues'];
  const filterOptions = useSelector((state) =>
    selectRptReportFilterOptions(state, reportKey),
  )! as Report['filterOptions'];

  const handleLoadReportCsv = React.useCallback(() => {
    dispatch(
      doReportingGetReportCsv({
        key: reportKey,
      }),
    );
  }, [dispatch, reportKey]);

  // Search field
  const handleSearchChange = React.useCallback(
    (newSearchValue: string) =>
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: { search: newSearchValue },
          loadData: true,
        }),
      ),
    [dispatch, reportKey],
  );

  const filteredOrganisations = filterValues.organisationIds.length
    ? filterOptions.organisations.filter((item) =>
        filterValues.organisationIds.includes(item.value),
      )
    : filterOptions.organisations.length === 1
      ? filterOptions.organisations
      : [];

  return (
    <>
      <PageHeader
        title={reportName}
        subtitle={
          pluralise(
            reportTotalCount,
            '{x} Result',
            '{x} Results',
            'No Results',
          ) +
          (filteredOrganisations.length > 1
            ? ` • ${filteredOrganisations.length} Organisations`
            : filteredOrganisations.length === 1
              ? ` • ${filteredOrganisations[0]!.name}`
              : ' • All Organisations')
        }
        showSidebarButton
        menuRight={{
          iconButtonConfig: { icon: ICONS.EllipsisLight },
          options: [
            {
              text: 'Export List as CSV',
              icon: ICONS.ArrowDownLight,
              onPress: handleLoadReportCsv,
            },
            <CardRow
              key="visualsOptionsRow"
              label="Visuals Options"
              disabled={!hasVisuals}
              navigationIcon={ICONS.ChartBarDuotone}
              navigationIconColor={theme.colors.black}
              style={{
                width: 250,
                minHeight: 44,
                backgroundColor: '#fafafa',
              }}
              hoveredStyle={{ backgroundColor: '#f1f1f1' }}
              childrenStyle={{}}
              menuOptions={
                hasVisuals
                  ? [
                      {
                        text: 'Large Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.large
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.large,
                            }),
                          ),
                      },
                      {
                        text: 'Small Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.small
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.small,
                            }),
                          ),
                      },
                      {
                        text: 'Hide Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.none
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.none,
                            }),
                          ),
                      },
                      smartText(
                        'Some reports include visuals like charts and graphs. They only appear if they have data to show',
                      ),
                    ]
                  : undefined
              }
              showMenuOnPress
              hideMenuButton
            />,
          ],
        }}
      />
      <PageToolbar
        onSearch={handleSearchChange}
        searchValue={filterValues.search}
        searchPlaceholder={`Search ${reportName}`}
        searchOnSubmit
      >
        <ButtonGroup>
          <IconButton
            icon={ICONS.RotateRightLight}
            onPress={() =>
              dispatch(
                doReportingGetReportListData({
                  key: reportKey,
                  nextPage: false,
                  closeModals: false,
                  overlayLoader: true,
                }),
              )
            }
          />
          <IconButton
            icon={ICONS.SlidersUpLight}
            onPress={() =>
              dispatch(doReportingShowReportFilterModal({ key: reportKey }))
            }
            showBadge={!!filterValues.organisationIds.length}
          />
        </ButtonGroup>
      </PageToolbar>
    </>
  );
};
